import { apiUrlV2 } from "../constants/apiUrl"
import AuditUnitFormData from "../components/VCI/AuditUnit/AuditUnitFormData"
import { convertImagesToBase64 } from "../utilities/convertImagesToBase64"
import { createFileFromBase64 } from "../utilities/createFileFromBase64"
import rollbarLogger from "../utilities/rollbarLogger"

const initialState = {
  fieldAudit: null,
  currentFieldAudit: {},
  fieldAuditUnits: {},
  auditUnit: null,
  followUpAudits: [],
  auditsPaginationData: {},
  auditUnitFiles: []
}

const createFieldAuditsSlice = (set, get) => ({
  ...initialState,
  fetchFollowUpAudits: async (page = 0, onSuccess = null) => {
    const params = new URLSearchParams({
      branch_id: get().selectedBranch[0],
      page: page,
      per_page: 5,
      'filter[status_by]': get().user.userId,
      'filter[audit_type]': 3,
      'filter[scope]': 'in_progress',
      'sort[date_started]': true,
      'sort[date_started_direction]': 'DESC'
    }).toString()
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits?${params}`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()
      set(() => ({
        followUpAudits: data.data,
        auditsPaginationData: {
          currentPage: Number(response.headers.get('x-page')) - 1,
          lastPage: Number(response.headers.get('x-last-page')),
          dataTotal: Number(response.headers.get('x-total')),
          totalPages: Number(response.headers.get('x-total-pages'))
        }
      }))
      if (onSuccess) { onSuccess(response) }
    } catch (err) {
      get().logError('Unable to retrieve follow up audits at this time.', err)
    }
  },
  fetchFieldAudit: async (id, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits/${id}`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()
      set(() => ({ fieldAudit: data.data }))
      if (onSuccess) { onSuccess() }
    } catch (err) {
      get().logError('Unable to retrieve field audit at this time.', err)
    }
  },
  createFieldAudit: async (body, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits`,
        {
          headers: get().requestHeaders,
          method: 'POST',
          body: JSON.stringify(body)
        }
      )
      const data = await response.json()

      if (response.status === 200) {
        set((state) => ({ fieldAudit: data.data }))
        if (onSuccess) { onSuccess(data.data) }
      } else {
        get().setErrMessage(data?.error || data?.errors?.[0] || 'There was a server error creating this field audit. Please try again later.')
      }
    } catch (err) {
      get().setErrMessage('There was an error creating this field audit.')
      get().logError('There was an error creating this field audit.', err)
    }
  },
  updateFieldAudit: async (id, body, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits/${id}`,
        {
          headers: get().requestHeaders,
          method: 'PUT',
          body: JSON.stringify({ eaudit: body })
        }
      )
      const data = await response.json()
      set(() => ({ fieldAudit: data.data }))
      if (onSuccess) { onSuccess() }
    } catch (err) {
      get().logError('Unable to update field audit at this time.', err)
    }
  },
  fetchFieldAuditUnits: async (id, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits/${id}/units?per_page=300`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()
      set(() => ({ fieldAuditUnits: data }))
      if (onSuccess) { onSuccess() }
    } catch (err) {
      get().logError('Unable to retrieve field audit units at this time.', err)
    }
  },
  fetchAuditUnit: async (fieldAuditId, auditUnitId, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits/${fieldAuditId}/units/${auditUnitId}`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()
      set(() => ({ auditUnit: data.data }))
      if (onSuccess) { onSuccess() }
    } catch (err) {
      get().logError('Unable to retrieve audit unit at this time.', err)
    }
  },
  updateAuditUnit: async (fieldAuditId, auditUnitId, auditData, images, onSuccess = null) => {
    const requestId = `update-audit-unit-${fieldAuditId}-${auditUnitId}`
    const logContext = { requestId, fieldAuditId, auditUnitId, imageKeys: images && typeof images === 'object' ? Object.keys(images) : images }

    const headers = {...get().requestHeaders}
    delete headers['Content-Type']

    // Local preparation runs synchronously (awaited by the caller) so pre-send failures can
    // keep the user on the confirm screen. Anything that fails here means the request was
    // never dispatched, so we return false and the caller does not navigate away.
    let processedImages = images
    if (images && typeof images === 'object' && Object.values(images).some(val => typeof val === 'string')) {
      try {
        processedImages = {}
        for (const [filename, fileOrString] of Object.entries(images)) {
          if (fileOrString && typeof fileOrString === 'string') {
            processedImages[filename] = await createFileFromBase64(filename, fileOrString)
          } else {
            processedImages[filename] = fileOrString
          }
        }
      } catch (imageError) {
        get().updateRequestStatus(requestId, 'failed')
        get().setErrMessage('We were unable to process the photos for this audit. Please review your photos and try again.')
        rollbarLogger(get().user).logError('Audit unit update failed while rebuilding images from base64. The request was never sent to the backend.', { ...logContext, errorName: imageError?.name, errorMessage: imageError?.message })
        return false
      }
    }

    let formData
    try {
      formData = await AuditUnitFormData({ auditData, images: processedImages, user: get().user })
    } catch (formDataError) {
      get().updateRequestStatus(requestId, 'failed')
      get().setErrMessage('We were unable to prepare this audit for submission. Please review your entries and try again.')
      rollbarLogger(get().user).logError('Audit unit update failed while building form data. The request was never sent to the backend.', { ...logContext, errorName: formDataError?.name, errorMessage: formDataError?.message })
      return false
    }

    try {
      get().enqueueRequest({
        id: requestId,
        action: 'updateAuditUnit',
        arguments: [
          fieldAuditId,
          auditUnitId,
          auditData,
          images ? await convertImagesToBase64(images) : images
        ],
        status: 'pending',
        meta: { fieldAuditId: fieldAuditId, auditUnitId: auditUnitId }
      })
    } catch (enqueueError) {
      get().updateRequestStatus(requestId, 'failed')
      get().setErrMessage('We were unable to prepare this audit for submission. Please review your entries and try again.')
      rollbarLogger(get().user).logError('Audit unit update failed while enqueuing the request. The request was never sent to the backend.', { ...logContext, errorName: enqueueError?.name, errorMessage: enqueueError?.message })
      return false
    }

    // The request is now prepared and queued. Fire it in the background (not awaited) so the
    // user can keep auditing other units while the upload runs. The queue tracks its status.
    const sendRequest = async () => {
      try {
        const response = await fetch(
          `${apiUrlV2}/field_audits/${fieldAuditId}/units/${auditUnitId}`,
          {
            headers: headers,
            method: 'PUT',
            body: formData
          }
        )
        const responseData = await response.json()

        if (response.status === 200) {
          set((state) => ({
            auditUnit: null,
            fieldAuditUnits: {
              ...state.fieldAuditUnits,
              data: [
                ...(state.fieldAuditUnits?.data ?? []).filter(unit => String(unit.id) !== String(auditUnitId)),
                ...(responseData?.data ? [responseData.data] : [])
              ]
            }
          }))

          if (!responseData?.data) {
            rollbarLogger(get().user).logError('Audit unit update returned a 200 without a data payload.', { ...logContext, responseData })
          }

          get().markRequestSuccess(requestId)
          if (onSuccess) { onSuccess() }
        } else {
          get().updateRequestStatus(requestId, 'failed')
          rollbarLogger(get().user).logError(`Audit unit update was rejected by the backend with status ${response.status}.`, { ...logContext, status: response.status, errors: responseData?.errors ?? responseData })
        }
      } catch (err) {
        get().updateRequestStatus(requestId, 'failed')
        rollbarLogger(get().user).logError('Audit unit update failed after the request was dispatched (network or response parsing error).', { ...logContext, errorName: err?.name, errorMessage: err?.message })
      }
    }

    sendRequest()

    return true
  },
  completeFieldAudit: async (id, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits/${id}/complete`,
        {
          headers: get().requestHeaders,
          method: 'PUT'
        }
      )
      const data = await response.json()
      if (response.status === 200) {
        set(() => ({ fieldAudit: data.data }))
        if (onSuccess) { onSuccess() }
      } else {
        get().setErrMessage(data?.errors?.[0] || data?.error || 'There was a server error completing this field audit. Please try again later.')
      }
    } catch (err) {
      get().setErrMessage('Unable to complete field audit at this time.')
      get().logError('Unable to complete field audit at this time.', err)
    }
  },
  cancelFollowUpAudit: async (id, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/field_audits/${id}/cancel_follow_up`,
        {
          headers: get().requestHeaders,
          method: 'PUT'
        }
      )
      const data = await response.json()
      if (response.status === 200) {
        set((state) => ({
          followUpAudits: state.followUpAudits.filter((followUpAudit) => followUpAudit.id !== id)
        }))
        if (onSuccess) { onSuccess() }
      } else {
        get().setErrMessage(data?.errors?.[0] || data?.error || 'There was a server error canceling this follow up audit. Please try again later.')
      }
    } catch (err) {
      get().setErrMessage('Unable to cancel follow up audit at this time.')
      get().logError('Unable to cancel follow up audit at this time.', err)
    }
  },
  fetchAuditUnitFiles: async (auditUnitId, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/audit_units/${auditUnitId}/unit_files`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()
      set(() => ({ auditUnitFiles: data?.data ?? [] }))
      if (onSuccess) { onSuccess() }
    } catch (err) {
      get().logError('Unable to retrieve audit unit files at this time.', err)
    }
  },
  clearAuditUnitFiles: () => set({ auditUnitFiles: [] }),
  deleteAuditUnitFile: async (auditUnitId, fileId, onSuccess = null) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/audit_units/${auditUnitId}/unit_files/${fileId}`,
        { headers: get().requestHeaders, method: 'DELETE' }
      )
      if (onSuccess) { onSuccess() }
    } catch (err) {
      get().logError('There was an error deleting the audit unit file.', err)
    }
  },
  setLotPhotoRequired: (isLotPhotoRequired) => set((state) => {
    if (!state.fieldAudit?.attributes?.dealer) { return {} }

    return {
      fieldAudit: {
        ...state.fieldAudit,
        attributes: {
          ...state.fieldAudit.attributes,
          dealer: {
            ...state.fieldAudit.attributes.dealer,
            lot_photo_required: isLotPhotoRequired
          }
        }
      }
    }
  }),
  setAuditUnit: (auditUnit) => set({ auditUnit }),
  setCurrentFieldAudit: (fieldAudit) => set({ currentFieldAudit: fieldAudit }),
  resetFieldAuditsSlice: () => set(initialState)
})

export default createFieldAuditsSlice